import type { Directive } from 'vue';
import lang from '~/core/lang';

interface TooltipState {
    floating?: HTMLElement;
    text?: HTMLElement;
    arrow?: HTMLElement;
    cleanup?: () => void;
    hideTimeout?: ReturnType<typeof setTimeout>;
    hiddenTimeout?: ReturnType<typeof setTimeout>;
    isVisible: boolean;
}

let floatingUi: Promise<typeof import('@floating-ui/dom')> | undefined;

function loadFloatingUi(): Promise<typeof import('@floating-ui/dom')> {
    floatingUi ??= import('@floating-ui/dom');

    return floatingUi;
}

function createTooltip(el: HTMLElement, value: any): TooltipState {
    const [path, placement = 'bottom', alreadyTranslated] = Array.isArray(value) ? value : [value, 'bottom'];
    const content = alreadyTranslated ? path : lang.global.t(path);

    const floatingElement = document.createElement('div');
    floatingElement.className = 'floating-tooltip';
    // Text lives in its own span so updates (see setTooltip) can replace it
    // without touching the sibling arrow element appended below.
    const textElement = document.createElement('span');
    textElement.textContent = content;
    floatingElement.appendChild(textElement);
    floatingElement.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        background: var(--color-primary-900);
        color: var(--color-primary-50);
        padding: 8px 12px;
        border-radius: var(--radius);
        font-size: var(--text-sm);
        line-height: 1.4;
        z-index: 9999;
        max-width: 300px;
        word-wrap: break-word;
        white-space: pre-line;
        pointer-events: none;
        border: 1px solid color-mix(in srgb, var(--color-primary-400) 20%, transparent);
    `;

    const arrowElement = document.createElement('div');
    arrowElement.className = 'floating-tooltip-arrow';
    arrowElement.style.cssText = `
        position: absolute;
        width: 8px;
        height: 8px;
        background: var(--color-primary-900);
        transform: rotate(45deg);
        z-index: -1;
    `;
    floatingElement.appendChild(arrowElement);

    const state: TooltipState = {
        floating: floatingElement,
        text: textElement,
        arrow: arrowElement,
        isVisible: false,
    };

    const updatePosition = () => {
        if (!state.floating || !el || el.parentNode == null || !el.checkVisibility()) return;

        void loadFloatingUi().then(({ arrow, computePosition, flip, offset, shift }) => {
            if (!state.floating) return;

            computePosition(el, state.floating, {
                placement: placement as any,
                middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowElement })],
            }).then(({ x, y, placement: finalPlacement, middlewareData }) => {
                if (!state.floating) return;

                state.floating.style.left = `${x}px`;
                state.floating.style.top = `${y}px`;

                if (middlewareData.arrow) {
                    const { x: arrowX, y: arrowY } = middlewareData.arrow;
                    const side = finalPlacement.split('-')[0];

                    const staticSide = {
                        top: 'bottom',
                        right: 'left',
                        bottom: 'top',
                        left: 'right',
                    }[side];

                    Object.assign(arrowElement.style, {
                        left: arrowX != null ? `${arrowX}px` : '',
                        top: arrowY != null ? `${arrowY}px` : '',
                        right: '',
                        bottom: '',
                        [staticSide as string]: '-4px',
                    });
                }
            });
        });
    };

    const show = () => {
        if (!state.floating) return;

        if (state.hideTimeout) {
            clearTimeout(state.hideTimeout);
            state.hideTimeout = undefined;
        }

        if (state.hiddenTimeout) {
            clearTimeout(state.hiddenTimeout);
            state.hiddenTimeout = undefined;
        }

        state.isVisible = true;
        updatePosition();
        state.floating.classList.add('visible');

        document.body.appendChild(state.floating);

        void loadFloatingUi().then(({ autoUpdate }) => {
            if (!state.floating || !state.isVisible) return;

            state.cleanup = autoUpdate(el, state.floating, updatePosition);
        });
    };

    const hide = () => {
        if (!state.floating) return;

        if (state.hideTimeout) {
            clearTimeout(state.hideTimeout);
        }

        if (state.hiddenTimeout) {
            clearTimeout(state.hiddenTimeout);
        }

        state.hideTimeout = setTimeout(() => {
            if (!state.floating) return;

            state.isVisible = false;
            state.floating.classList.remove('visible');

            state.hiddenTimeout = setTimeout(() => {
                if (!state.floating) return;
                state.floating.remove();
            }, 150);

            if (state.cleanup) {
                state.cleanup();
                state.cleanup = undefined;
            }
        }, 100);
    };

    const hideOnBlur = () => {
        if (el.matches(':hover')) return;

        hide();
    };

    el.addEventListener('mouseenter', show);
    el.addEventListener('mouseleave', hide);
    el.addEventListener('focus', show);
    el.addEventListener('blur', hideOnBlur);

    return state;
}

function destroyTooltip(state: TooltipState) {
    if (state.hideTimeout) {
        clearTimeout(state.hideTimeout);
    }
    if (state.hiddenTimeout) {
        clearTimeout(state.hiddenTimeout);
    }
    if (state.cleanup) {
        state.cleanup();
    }

    state.floating?.remove();
    delete state.floating;

    state.text?.remove();
    delete state.text;

    state.arrow?.remove();
    delete state.arrow;
}

function setTooltip(el: any, value: any) {
    if (el._setTooltip) return;

    el._setTooltip = setTimeout(() => {
        if (el._tooltipState) {
            const [path, , alreadyTranslated] = Array.isArray(value) ? value : [value, 'bottom'];
            const content = alreadyTranslated ? path : lang.global.t(path);
            // Update the dedicated text span, not the whole tooltip's
            // textContent (which would wipe the sibling arrow). Live consumers
            // like the thread-usage card re-bind every tick.
            if (el._tooltipState.text) {
                el._tooltipState.text.textContent = content;
            }
        } else {
            el._tooltipState = createTooltip(el, value);
        }
        delete el._setTooltip;
    }, 0);
}

const directive: Directive = {
    mounted: (el, binding) => {
        if (!binding.value) return;
        setTooltip(el, binding.value);
    },

    updated: (el, binding) => {
        if (!binding.value) {
            if (el._setTooltip) {
                clearTimeout(el._setTooltip);
                delete el._setTooltip;
            }

            if (el._tooltipState) {
                destroyTooltip(el._tooltipState);
                delete el._tooltipState;
            }
            return;
        }

        setTooltip(el, binding.value);
    },

    beforeUnmount: (el) => {
        if (el._setTooltip) {
            clearTimeout(el._setTooltip);
            delete el._setTooltip;
        }

        if (el._tooltipState) {
            destroyTooltip(el._tooltipState);
            delete el._tooltipState;
        }
    },
};

export default directive;
